/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { Playlist, ResourceTag } from '../../../shared/models';
import {
  CreateFrameworkRequest,
  Framework,
  FrameworkCategoriesTree,
  FrameworkDetails,
  OrganizationFramework,
  UpdateFrameworkRequest,
} from '../../../shared/models/admin/admin.model';
import {
  CreateFrameworkV3Request,
  FrameworkLibraryEntryV3,
  FrameworkV3,
  UpdateFrameworkV3Request,
} from '../../../shared/models/admin/framework-v3.model';
import { UntaggedContentDetails } from '../../../shared/models/admin/skills-tagging-bulk.model';
import {
  SkillsTaggingCourseDetails,
  SkillsTaggingCoursesQuery,
  SkillsTaggingCoursesStatsDetails,
  SkillsTaggingEventDetails,
  SkillsTaggingEventsQuery,
  SkillsTaggingProgramme,
  SkillsTaggingStatsDetails,
} from '../../../shared/models/admin/skills-tagging.model';
import { Page as Pagination } from '../../../shared/models/page';
import {
  CategoryItemCreationRequest,
  CategoryItemCreationResponse,
  Page,
  PageRequest,
  Section,
  SectionReorder,
  SectionRequest,
} from '../../../shared/models/page/page.model';
import { ObservableResult } from '../../../shared/store';
import { AiSkillsContentType } from '../../../standalone/ai/models/skills-extraction.model';

/** Injection token for the data service, used by angular to decide whether to inject Mock or API service at runtime. */
export const ADMIN_DATA_SERVICE = new InjectionToken<AdminDataService>('[Admin] DataService');

/** The admin data service. */
export interface AdminDataService {
  /** Loads the frameworks published for the organization. */
  loadFrameworks(): ObservableResult<Framework[]>;

  /** Loads the frameworks active for the organization and published, hydrated with their tags tree. */
  getFrameworksV3(): ObservableResult<FrameworkDetails[]>;

  /** Loads the catalog of frameworks the organization can add, hydrated with their tags tree. */
  loadFrameworkLibrary(): ObservableResult<FrameworkDetails[]>;

  /** Loads the content store frameworks for a user. */
  loadContentStoreFrameworks(): ObservableResult<Framework>;

  /** Loads the discovery frameworks for a user. */
  loadFrameworkCategories(): ObservableResult<Framework>;

  loadFrameworkCategoriesTree(): ObservableResult<FrameworkCategoriesTree[]>;

  /** Loads the explore categories for a user. */
  loadCategoriesDescendantsTags(): ObservableResult<ResourceTag[]>;

  loadCategoryChildren(categoryTagUid: string): ObservableResult<ResourceTag[]>;

  /** Loads the framework for a user by framework id. */
  loadFrameworkById(frameworkId: string): ObservableResult<FrameworkDetails>;

  /** Loads the framework tags descendants for a user by framework id. */
  loadFrameworkDescendants(frameworkId: string): ObservableResult<ResourceTag[]>;

  /** Loads the framework tag descendants for a user by tag id. */
  loadFrameworkTagDescendants(frameworkId: string, tagId: string): ObservableResult<ResourceTag[]>;

  /** Updates the framework. */
  updateFramework(frameworkId: string, frameworkParams: UpdateFrameworkRequest): ObservableResult<Framework>;

  /** Creates a new framework. */
  createFramework(frameworkParams: CreateFrameworkRequest): ObservableResult<void>;

  /** Set the framework published or unpublished. */
  publishFramework(frameworkId: string, publish: boolean): ObservableResult<Framework>;

  /** Archives or unarchives an organization-owned framework. */
  changeFrameworkArchivedStatus(frameworkId: string, archived: boolean): ObservableResult<Framework>;

  /** Loads the frameworks of the organization for the V3 admin list page. */
  loadFrameworksV3List(): ObservableResult<FrameworkV3[]>;

  /** Loads the pre-loaded framework library entries for the V3 admin library page. */
  loadFrameworkLibraryV3(): ObservableResult<FrameworkLibraryEntryV3[]>;

  /** Loads a single framework with its full tag tree for the V3 admin editor. */
  loadFrameworkByIdV3(frameworkId: string): ObservableResult<FrameworkV3>;

  /** Creates a new framework from the V3 admin editor. */
  createFrameworkV3(request: CreateFrameworkV3Request): ObservableResult<FrameworkV3 | void>;

  /** Updates a framework from the V3 admin editor. */
  updateFrameworkV3(frameworkId: string, request: UpdateFrameworkV3Request): ObservableResult<FrameworkV3>;

  /** Loads all frameworks added to the organization, whether switched on or not. */
  loadOrganizationFrameworks(): ObservableResult<OrganizationFramework[]>;

  /** Loads the frameworks added to the organization and switched on. */
  loadActiveOrganizationFrameworks(): ObservableResult<OrganizationFramework[]>;

  /** Adds framework to Organization. */
  addFrameworkToOrganization(frameworkId: string): ObservableResult<OrganizationFramework>;

  /** Removes framework from Organization. */
  removeFrameworkFromOrganization(frameworkId: string): ObservableResult<void>;

  /** Sets organization framework status. */
  setOrganizationFrameworkStatus(frameworkId: string, status: boolean): ObservableResult<OrganizationFramework>;

  /** Updates the page. */
  updatePage(pageUid: string, pageBody: PageRequest): ObservableResult<Page>;

  /** Creates a new section. */
  addSection(pageUid: string, sectionBody: SectionRequest, languageCode?: string): ObservableResult<Section>;

  /** Removes the section. */
  removeSection(pageUid: string, sectionUid: string): ObservableResult<void>;

  /** Updates the section. */
  updateSection(pageUid: string, sectionUid: string, section: SectionRequest, languageCode?: string): ObservableResult<Section>;

  /** Bulk updates multiple sections on the page. */
  bulkUpdateSections(pageUid: string, sections: SectionRequest[]): ObservableResult<Section[]>;

  /** Reorders sections on the page. */
  reorderPageSections(pageUid: string, section: SectionReorder): ObservableResult<Section[]>;

  /** Returns latest playlists for given user from organization. */
  getLatestPlaylists(
    page: number,
    size: number,
    frameworkId?: string,
    tagId?: string,
    languageCode?: string,
  ): ObservableResult<Pagination<Playlist>>;

  getPlaylistsByTags(page: number, size: number, tagIds?: string, languageCode?: string): ObservableResult<Pagination<Playlist>>;

  /** Returns uncompleted learners playlists list */
  getUncompletedPlaylists(page: number, size: number, languageCode?: string): ObservableResult<Pagination<Playlist>>;

  getYourPlaylists(page: number, size: number, term?: string, languageCode?: string): ObservableResult<Pagination<Playlist>>;

  /** Add category. */
  addCategory(pageUid: string, sectionUid: string, request: CategoryItemCreationRequest): ObservableResult<CategoryItemCreationResponse>;

  /** Update category. */
  updateCategory(
    pageUid: string,
    sectionUid: string,
    categoryId: string,
    request: CategoryItemCreationRequest,
  ): ObservableResult<CategoryItemCreationResponse>;

  /** Remove category. */
  removeCategory(pageUid: string, sectionUid: string, categoryId: string): ObservableResult<void>;

  cloneSection(pageUid: string, sectionUid: string): ObservableResult<Section>;

  createLtiFramework(requestId: string | null, organizationUid: string, frameworkParams: CreateFrameworkRequest): ObservableResult<void>;

  loadLtiFrameworks(requestId: string | null, organizationUid: string): ObservableResult<Framework[]>;

  getSkillsTaggingCoursesStats(): Observable<SkillsTaggingCoursesStatsDetails>;

  getSkillsTaggingCourses(query: SkillsTaggingCoursesQuery): Observable<Pagination<SkillsTaggingCourseDetails>>;

  getSkillsTaggingEventsStats(): Observable<SkillsTaggingStatsDetails>;

  getSkillsTaggingEvents(query: SkillsTaggingEventsQuery): Observable<Pagination<SkillsTaggingEventDetails>>;

  getSkillsTaggingProgrammes(): Observable<SkillsTaggingProgramme[]>;

  getUntaggedContent(contentTypes?: AiSkillsContentType[]): Observable<UntaggedContentDetails[]>;
}
